import { useApi } from "@utils/useApi";
import { OrdersClient } from "@lib/ShiOneClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { OrdersQueryKeys } from "@features/orders-features/hooks/OrdersQueryKeys";
import ms from "ms";
import { useCallback } from "react";

export function useGetShipmentTracking(
  biOrderId: number | undefined | null,
  trackingNumber: string | undefined | null,
) {
  const api = useApi(OrdersClient);
  const enabled = !!biOrderId && !!trackingNumber;

  const fetchShipmentTracking = async () => {
    return await api.getShipmentTracking(biOrderId!, trackingNumber!);
  };

  return useQuery({
    queryKey: [OrdersQueryKeys.ShipmentTracking, biOrderId, trackingNumber],
    queryFn: fetchShipmentTracking,
    enabled,
    staleTime: ms("2m"),
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Returns a function that warms the shipment-tracking cache for a given
 * shipment. Used to prefetch tracking when the order drawer opens so clicking
 * "Track Package" resolves instantly. Uses the same query key as
 * {@link useGetShipmentTracking} so the drawer reads straight from cache.
 */
export function usePrefetchShipmentTracking() {
  const api = useApi(OrdersClient);
  const queryClient = useQueryClient();

  return useCallback(
    (
      biOrderId: number | undefined | null,
      trackingNumber: string | undefined | null,
    ) => {
      if (!biOrderId || !trackingNumber) return;

      void queryClient.prefetchQuery({
        queryKey: [OrdersQueryKeys.ShipmentTracking, biOrderId, trackingNumber],
        queryFn: () => api.getShipmentTracking(biOrderId, trackingNumber),
        staleTime: ms("2m"),
      });
    },
    [api, queryClient],
  );
}

